// triggeredListViewModel.js — m3-16a Triggered Tasks / Agent Jobs LIST logic (pure, node-testable).
//
// LIST ONLY: rows + on/off toggle state + the count line + the preset carousel display +
// carousel arrow enable/disable. NOT the builder (m3-16b — deferred, chair, blocked on m3-01)
// and NOT the persistence/firing path (slice 12 / m3-16b). This module shapes what the list
// renders and computes toggle/count derivations; the actual backend-enforced stop on toggle-off
// is m3-16b (DESIGN-INTEGRATION §2.4 — "Toggle off = backend-enforced stop").
//
// Field shape preserved from the design (docs/05 / cc-data AUTOMATIONS):
//   { id, name, kind: 'schedule'|'event'|'condition', enabled, trigger, icon, action,
//     scope: [channelId...], last?, nextRun? }
// `condition` kind is DEFERRED for V1 (needs a metrics rule-engine) — the list still renders
// a seeded condition row if present, but the builder won't author new ones (m3-16b).

import { buildSliderPresets, getRegistry } from "./libraryViewModel.js"
// A3: reused ONLY for availableEfforts (haiku -> [] -> effort omitted), mirroring buildSendBody's
// per-model effort gating for the job-level Model/Effort override control. studioShellViewModel.js
// has no import of this module — safe, no cyclic import.
import { availableEfforts } from "./studioShellViewModel.js"

// Item #6 (2026-07-28): 4th kind 'chat' — a job the user invokes by referencing it in chat, like a
// skill. No machine trigger (backend normalize_chat -> {}), no next run, never tick-selected.
export const KIND_ICON = Object.freeze({ schedule: "clock", event: "zap", condition: "sliders", chat: "send" })

// The one human-readable trigger line every chat-kind surface renders (row, preview, describe).
// Kept as a single exported constant so the label can never fork across the ≥6 kind switches.
export const CHAT_TRIGGER_LABEL = "When you ask in chat"

// Seed rows — matched to cc-data AUTOMATIONS canonical strings (TRIGGERED-SPEC-v2 §T-P2 #20).
// Trigger strings now include "CT" suffix + "in Vesta"; newevent icon zap→sparkle;
// guardrail icon sliders→shield.
// NOTE (Item 6 r2): these are DISPLAY-ONLY demo rows — their `scope` arrays feed CHANNEL_NAMES
// labels, never an AgentJob, so `"google"` here (not in Session::CHANNELS either) cannot reproduce
// the lost-firing bug the calendars drop fixed. If a seed row ever becomes creatable as a real
// job, its scope must first be trimmed to AgentJob::AGENT_CHANNELS.
export const SEED_AUTOMATIONS = Object.freeze([
  { id: "meta-creative", name: "Meta creative monitor", kind: "schedule", enabled: true,
    trigger: "Every day · 9:00 AM CT", icon: "sparkle",
    action: "Watch Meta ad creative for fatigue, refresh tired assets, and keep an A/B test running on fresh variants. Meta ads only.",
    scope: ["meta"], last: "Today, 9:01 AM", nextRun: "Tomorrow, 9:00 AM" },
  { id: "newevent", name: "New event → launch plan", kind: "event", enabled: true,
    trigger: "When an event is published in Vesta", icon: "sparkle",
    action: "Draft campaigns across active channels, add to all calendars, queue an on-sale burst.",
    scope: ["google", "meta", "social"], last: "2h ago · Sunset Sessions Vol. 9", nextRun: "On next published event" },
  { id: "guardrail", name: "Meta ads ROAS guardrail", kind: "condition", enabled: true,
    trigger: "When a Meta ads campaign's ROAS drops below 2.0x for 3 days", icon: "shield",
    action: "Pause the Meta ads campaign and notify you before any further spend.",
    scope: ["meta"], last: "Never triggered", nextRun: "Continuously" },
  { id: "social-review", name: "Social content calendar review", kind: "schedule", enabled: true,
    trigger: "Every Monday · 9:00 AM CT", icon: "calendar",
    action: "Review the week's agent-written social posts: edit, approve, or reject each one before it's scheduled to publish.",
    scope: ["social"], last: "Monday, 9:04 AM", nextRun: "Monday, 9:00 AM" },
  { id: "digest", name: "Weekly performance digest", kind: "schedule", enabled: false,
    trigger: "Mondays · 9:00 AM CT", icon: "chart",
    action: "Email a plain-English recap of spend, tickets and ROAS.",
    scope: ["google", "meta", "social"], last: "Paused", nextRun: "Disabled" },
])

// TASK 9: the hardcoded `SUGGESTIONS` preset array (task 10's honest 5-card set) has been RETIRED —
// the Agent Jobs preset carousel now derives from the single checked-in library registry via
// libraryViewModel.buildSliderPresets (see buildPresets below). The registry preserves task 10's
// honest, de-cadenced copy (no lookalike, no banned oversell phrases) and adds the raw-trigger
// passthrough seed each preset carries into NewAgentForm's library-trigger mode.

export const CHANNEL_NAMES = Object.freeze({
  meta: "Meta Ads", social: "Social Media", email: "Email", calendars: "Event Calendars",
  google: "Google Ads", vesta: "Vesta",
})

// ── Item #6 taste round 1 — ONE channel-chip grammar: checked ALWAYS means "allowed" ──────────
// Product semantics for agent_jobs.scope: an EMPTY scope means "no restriction — every channel"
// (the form's own helper copy — "Leave all on to let it work across everything" — and the agent's
// in-chat description of a default job both say so). The fresh-create form used to render that
// implicit all-on state as four PLAIN chips while library seeds rendered explicit scopes with
// checkmarks — two grammars for one control. These two pure helpers make the chip row speak the
// stored meaning: an empty scope renders every chip checked.
//
// NOTE (flagged, not fixed here): the ENFORCEMENT layer reads an empty session scope fail-closed
// (ScopeGuard + TS scope.ts mount no write channels for []), which contradicts the product-copy
// "empty = everything" reading for recurring jobs. That contradiction predates this change and is
// a register item — these helpers only make the chips consistent with the stated product meaning,
// and deliberately keep an untouched form's payload byte-identical (still []).

// Is the chip for channel `id` checked? Empty/absent scope = the implicit all-on state.
export function channelChipOn(scope, id) {
  const s = scope || []
  return s.length === 0 || s.includes(id)
}

// Flip exactly the clicked chip. From the implicit all-on state ([]), unchecking one chip
// materializes the explicit complement (all ids minus the clicked one). A toggle that lands on
// the COMPLETE set normalizes back to [] so an untouched-equivalent selection persists exactly
// as the seed stored it (and no channel enters a payload nobody explicitly narrowed).
export function toggleChannelScope(scope, id, allIds) {
  const effective = scope && scope.length > 0 ? scope : allIds
  const next = effective.includes(id)
    ? effective.filter((x) => x !== id)
    : [...effective, id]
  return next.length === allIds.length ? [] : next
}

// ── H-GATE (spec-H): real-API wiring — pure mappers ─────────────────────────────────────
// The coarse per-company feature gate that unlocks the WHOLE agent-job surface (server-side
// require_agent_jobs_gate! 403s without it; this constant is the frontend mirror).
export const AGENT_TRIGGERS_GATE = "agent-triggers"

// Neutral empty-state copy for a non-gated company (never SEED_AUTOMATIONS, never a crash).
export const GATE_BLOCKED_COPY =
  "Agent Jobs aren't enabled for this company — a platform admin can turn them on."

const DOW_NAME = Object.freeze([
  "Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday",
])
const TZ_LABEL = Object.freeze({
  "America/New_York": "ET", "America/Chicago": "CT",
  "America/Denver": "MT", "America/Los_Angeles": "PT",
})

// Serialized trigger_spec ({cron,tz} | {vesta_event_type}) + kind (+rule_key for condition)
// → the human-readable trigger line the row renders. Pure; unknown shapes degrade to a readable
// fallback (never throw — the list must render whatever the API stored).
// 1 → "1st", 2 → "2nd", 15 → "15th", 22 → "22nd" (day-of-month label for monthly schedules).
export function ordinal(n) {
  const v = n % 100
  if (v >= 11 && v <= 13) return `${n}th`
  return `${n}${({ 1: "st", 2: "nd", 3: "rd" })[n % 10] || "th"}`
}

export function describeTriggerSpec(kind, spec, ruleKey = null) {
  const s = spec || {}
  if (kind === "chat") return CHAT_TRIGGER_LABEL
  if (kind === "event") {
    if (s.vesta_event_type === "event_published") return "When an event is published in Vesta"
    return s.vesta_event_type ? `On: ${s.vesta_event_type}` : "On a Vesta event"
  }
  if (kind === "condition") {
    return ruleKey ? `When ${String(ruleKey).replace(/_/g, " ")}` : "Continuously"
  }
  // schedule — {cron:"m h * * dow", tz:"America/Chicago"} or the monthly shape {cron:"m h dom * *"}
  // (job-review-clarify-cadence). The monthly label ("Monthly on the 15th") is the SAME string the
  // form's SUPPORTED_SCHED_FREQ offers, so an edit round-trips through NewAgentForm's label split.
  const m = String(s.cron || "").trim().match(/^(\d{1,2})\s+(\d{1,2})\s+(\d{1,2}|\*)\s+\*\s+([0-6*])$/)
  if (!m) return s.cron ? `cron: ${s.cron}` : "On a schedule"
  const minute = Number(m[1])
  const hour = Number(m[2])
  const h12 = ((hour + 11) % 12) + 1
  const ampm = hour >= 12 ? "PM" : "AM"
  const time = `${h12}:${String(minute).padStart(2, "0")} ${ampm}`
  const day = m[3] !== "*"
    ? `Monthly on the ${ordinal(Number(m[3]))}`
    : (m[4] === "*" ? "Every day" : `Every ${DOW_NAME[Number(m[4])]}`)
  const tz = TZ_LABEL[s.tz] || (s.tz ? String(s.tz).split("/").pop().replace(/_/g, " ") : "")
  return tz ? `${day} · ${time} ${tz}` : `${day} · ${time}`
}

// One serialized AgentJob row (Api::Agent::AgentJobsController#index → AgentJobs::Create.serialize)
// → the task shape buildRow/toggleTask consume. Pure. isNew is always false (API rows are settled).
//
// D16 (grader-found blocker): a condition job's `trigger` is a DISPLAY string
// (describeTriggerSpec's fallback, e.g. "When roas below") that is NOT a COND_RULE_MAP key — editing
// that job and re-submitting via label-matching would always miss and false-block as "unsupported".
// So the RAW rule_key/rule_params/trigger_spec ride along on the task too; the edit path (NewAgentForm)
// uses THESE directly for an API-sourced condition job, never re-deriving via the display label.
export function apiJobToTask(job) {
  const j = job || {}
  return {
    id: j.id,
    name: j.name || j.title || "Agent job",
    kind: j.kind,
    enabled: !!j.enabled,
    trigger: describeTriggerSpec(j.kind, j.trigger_spec, j.rule_key),
    icon: j.icon || "sparkle",
    action: j.action || "",
    scope: j.scope || [],
    last: j.last_run_at ? String(j.last_run_at) : "Not run yet",
    // A chat job has no machine schedule — "On its next run" would be dishonest; it runs on request.
    nextRun: j.kind === "chat"
      ? (j.enabled ? "On request" : "Disabled")
      : (j.next_run_at ? String(j.next_run_at) : (j.enabled ? "On its next run" : "Disabled")),
    isNew: false,
    // raw API-sourced trigger data — carried for the edit round-trip (D16), never rendered directly
    ruleKey: j.rule_key || null,
    ruleParams: j.rule_params || null,
    triggerSpec: j.trigger_spec || null,
    // A4: the bound event id for a ticket-based condition job — carried for the edit round-trip so
    // the event picker pre-selects the job's stored event on open (mirrors ruleKey/ruleParams).
    eventId: j.event_id != null ? j.event_id : null,
    // B8: the job's most recent standing authorization (null for every job that never had one).
    // This mapper and buildRow below are both DROP FILTERS — a key absent from either never
    // reaches the row, which is the bug agentJobEditChain.test.mjs exists to catch.
    standingApproval: j.standing_approval || null,
    // NEXT Item 4: the NEGOTIATED context. Copied with KEY PRESENCE PRESERVED — `context: j.context
    // || null` would be WRONG here, because the form's M-R4-1 fail-safe distinguishes "the row has
    // no context key at all" (an un-wired serializer -> omit the key on PATCH so the server
    // preserves) from "the context is empty" (send "" to clear). Absent-key != empty-value.
    ...("context" in j ? { context: j.context } : {}),
  }
}

// Build a single row's view model.
// scope is preserved (not just labels) so the builder can pre-fill channel chips on edit.
export function buildRow(task) {
  return {
    id: task.id,
    name: task.name,
    action: task.action,
    enabled: !!task.enabled,
    kind: task.kind,
    kindIcon: KIND_ICON[task.kind] || "clock",
    trigger: task.trigger,
    icon: task.icon,
    scope: task.scope || [],
    // channel scope is a REAL capability boundary (DESIGN-INTEGRATION §2.4), surfaced as the
    // human-readable channel names the task may act on.
    scopeLabels: (task.scope || []).map((c) => CHANNEL_NAMES[c] || c),
    isNew: !!task.isNew,
    // preserve raw fields the builder needs for edit round-trip
    last: task.last,
    nextRun: task.nextRun,
    // D16 (iteration 5): the row IS the edit seed handed to NewAgentForm (openEdit(row)). The raw
    // API-sourced rule fields MUST ride through here or the condition-edit passthrough (apiCondition,
    // keyed off s0.ruleKey) never forms and a title-only edit false-blocks as "unsupported".
    ruleKey: task.ruleKey || null,
    ruleParams: task.ruleParams || null,
    triggerSpec: task.triggerSpec || null,
    // A4: carried onto the edit seed the same way ruleKey/ruleParams are (D16 precedent) — without
    // this the picker would silently drop a bound event on open-edit.
    eventId: task.eventId != null ? task.eventId : null,
    // B8: the standing-approval panel's data. Carried through for the same drop-filter reason.
    standingApproval: task.standingApproval || null,
    // NEXT Item 4: the negotiated context, key presence preserved (see apiJobToTask). buildRow is
    // the SECOND drop filter — the row IS the edit seed (s0), so a key missing here never reaches
    // the form no matter what the API sent.
    ...("context" in task ? { context: task.context } : {}),
  }
}

// NEXT Item 4 — the browser edit form's CONTEXT HYDRATION RULE (PLAN-NEXT §D.6), exported so the
// node tests can exercise it directly (NewAgentForm calls this and nothing else).
//
// The stored value is a jsonb HASH; the form field is a plain string textarea. Without this rule a
// browser edit either renders "[object Object]" or wipes agent-baked keys. THE RULE: the textarea
// owns ONLY the `notes` shape; structured contexts are AGENT-OWNED and the form never overwrites
// them.
//
//   hasContextKey — the M-R4-1 FAIL-SAFE. A seed that does not carry the key at all (an un-wired
//                   serializer, an old cached row) must OMIT the key on PATCH so the server's
//                   key-presence guard PRESERVES the stored value, instead of normalize("") wiping
//                   it. An isNotesOnly-only implementation classifies an absent key as notes-only
//                   and wipes — which is why this flag is separate.
//   isNotesOnly   — true for no context, a bare string, {} , or exactly {notes: …}. Anything else
//                   (budget/objective/…) renders read-only and is edited through the agent.
export function hydrateContext(s0) {
  const hasContextKey = !!s0 && ("context" in s0)
  const s0Ctx = s0 ? s0.context : undefined
  const isNotesOnly =
    !s0Ctx ||
    typeof s0Ctx === "string" ||
    (Object.keys(s0Ctx).length <= 1 && ("notes" in s0Ctx || Object.keys(s0Ctx).length === 0))
  const initialContext = typeof s0Ctx === "string" ? s0Ctx : (s0Ctx && s0Ctx.notes) || ""
  return { hasContextKey, isNotesOnly, initialContext }
}

// The read-only rendering of a STRUCTURED (agent-owned) context: "budget: $500 · objective: …".
export function describeContext(ctx) {
  if (!ctx || typeof ctx !== "object") return ""
  return Object.keys(ctx).map((k) => `${k}: ${ctx[k]}`).join(" · ")
}

// Build the whole list view model from the task array.
export function buildList(tasks) {
  const rows = (tasks || []).map(buildRow)
  const running = rows.filter((r) => r.enabled).length
  const paused = rows.length - running
  return {
    rows,
    running,
    paused,
    countLabel: `${running} running · ${paused} paused`,
  }
}

// Flip a task's enabled state by id — pure; returns a NEW array (the component sets state).
// In m3-16b this also POSTs the backend-enforced stop; here it's the optimistic UI flip only.
export function toggleTask(tasks, id) {
  return (tasks || []).map((t) => (t.id === id ? { ...t, enabled: !t.enabled } : t))
}

// Preset carousel display + arrow state. `scrollLeft`/`scrollWidth`/`clientWidth` come from the
// scroll container; the arrows disable at the ends (design docs/05).
export function carouselArrows({ scrollLeft = 0, scrollWidth = 0, clientWidth = 0 }) {
  const atStart = scrollLeft <= 0
  const atEnd = scrollLeft + clientWidth >= scrollWidth - 1
  return { prevDisabled: atStart, nextDisabled: atEnd }
}

// TASK 9: presets are now the registry's featured_slider job entries (buildSliderPresets already
// shapes name/icon/kind/trigger/short/action/scope + kindIcon/scopeLabels + a librarySeed the
// builder consumes in library-trigger mode). Export name preserved so TriggeredTasksList is a
// no-op re-point.
export function buildPresets() {
  return buildSliderPresets(getRegistry())
}

// buildTrigger — produces the human-readable trigger string for the preview row + submit payload.
// kind: 'schedule'|'event'|'condition'|'chat'; cfg: { freq, time, event, cond }
export function buildTrigger(kind, cfg) {
  if (kind === "schedule") return `${cfg.freq} · ${cfg.time}`
  if (kind === "event") return cfg.event
  if (kind === "chat") return CHAT_TRIGGER_LABEL
  return cfg.cond
}

// nextRunFor — the nextRun label for a newly created agent job. A chat job has NO next run (never
// tick-selected) — "On request" is the honest note, mirroring apiJobToTask's chat branch.
export function nextRunFor(kind) {
  if (kind === "schedule") return "On its next run"
  if (kind === "event") return "On next trigger"
  if (kind === "chat") return "On request"
  return "Continuously"
}

// ── D-wire: builder → real POST payload mapper (pure, node-testable) ───────────────────────
// AgentJobs::TriggerSpec.normalize only accepts a fixed vocabulary per kind (trigger_spec.rb).
// This maps the builder's raw cfg into the raw trigger_spec shape the backend normalizes, and
// friendly-blocks (client-side, no POST) the selections that are known-unsupported today.
//
// D9: these constants are also the SINGLE SOURCE OF TRUTH for which options NewAgentForm renders
// (imported there instead of NewAgentForm keeping its own, driftable copy) — verified against
// AgentJobs::TriggerSpec (trigger_spec.rb: parse_freq_dow L111-123 dow-only branches; EVENT_OPTIONS
// L47-50; CONDITION_RULE_KEYS L53-56) and docs/AGENT_JOB_TRIGGER_SUPPORT.md.

// The schedule frequencies the form offers: the 3 dow-only shapes parse_freq_dow accepts, plus
// monthly-on-the-Nth (cron "m h dom * *"), added by job-review-clarify-cadence — the 1st and 15th
// are the two day-of-month choices exposed here; cronLabel/formCfgForRawTrigger read any dom.
export const SUPPORTED_SCHED_FREQ = Object.freeze(["Every day", "Every weekday", "Every Monday", "Monthly on the 1st", "Monthly on the 15th"])

// The time choices the schedule panel offers (moved here from NewAgentForm so
// formCfgForRawTrigger below and the form share ONE list — the D9 non-drift pattern).
export const SUPPORTED_SCHED_TIME = Object.freeze(
  ["8:00 AM CT", "9:00 AM CT", "12:00 PM CT", "5:00 PM CT", "7:00 PM CT"]
)

// The only EVENT_OPTIONS entry with a normalize_event mapping (trigger_spec.rb L47-50).
export const SUPPORTED_EVENT_OPT = "When an event is published in Vesta"

// COND_RULE_MAP keys are the VERBATIM full option strings NewAgentForm's COND_OPTS stores in
// cfg.cond — NOT shorthand. "When an event sells out" (rule_key 'sold_out') is now creatable: its
// evaluator has been live (condition_evaluators.rb:26,44,109) and 'sold_out' joined
// TriggerSpec::CONDITION_RULE_KEYS (trigger_spec.rb:53-56).
//
// D12: each entry also carries the ruleParams the label implies, verified against what each
// evaluator actually reads (condition_evaluators.rb):
//   - pct_sold_below_with_days_left reads params['pct'] (a FRACTION, not a percent — sold_ratio is
//     sold/max_attendance) and params['days'] (evaluate_conditions.rb / condition_evaluators.rb:98-106).
//     "<50% sold with <14 days left" -> pct: 0.5, days: 14.
//   - roas_below reads params['threshold'] (condition_evaluators.rb:64-70). "2.0x" -> threshold: 2.0.
//     `window` mirrors the "for 3 days" clause and matches Meta::Insights::DEFAULT_DATE_PRESET
//     ('last_3d', insights.rb:55) — the fetch worker reads job.rule_params['window'] as the Graph
//     date_preset (evaluate_conditions.rb:175-176).
//
// D15: "When daily spend exceeds its cap" (spend_cap_exceeded) is intentionally ABSENT — the form
// has no cap-entry field, so any rule_params.cap this map could ship would be an unlabeled/implied
// placeholder value the user never actually chose (the old $300 default). Moved to
// docs/AGENT_JOB_TRIGGER_SUPPORT.md "not yet supported" with the honest blocker + fast-follow path.
// A4 — the picker's gate: which condition rule_keys are per-EVENT (need a bound `event_id` to ever
// leave :not_evaluable — condition_evaluators.rb's sold_out/pct_sold_below_with_days_left both read
// event.event_attendees/max_attendance via evaluate_conditions.rb's resolve_event(job.event_id)).
// Account-level rules (roas_below, spend_cap_exceeded) never show the picker and never carry
// event_id — this Set is the single source of truth NewAgentForm gates the picker's visibility on,
// in BOTH the !libraryMode (COND_OPTS) and libraryMode (rawTrigger) paths.
export const TICKET_RULE_KEYS = Object.freeze(new Set(["sold_out", "pct_sold_below_with_days_left"]))

// DESIGN-PANEL repair (A4 pass) — pure display/filter helpers for NewAgentForm's event picker.
// Kept here (a plain .js module) rather than inline in NewAgentForm.jsx so they're real
// unit-testable functions — NewAgentForm.jsx is JSX and can't be imported directly by node:test,
// so logic that lives only there can only ever be covered by a static-source regex scan.

const MONTH_ABBR = Object.freeze(["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"])

// Friendly short date ("Aug 7, 2026") for the event picker rows. Parsed from the server's plain
// YYYY-MM-DD (Event#start_date has no time component) via regex rather than `new Date(iso)` —
// `new Date("2026-08-07")` is UTC midnight, and formatting that with a LOCAL-timezone method
// shifts the displayed day backward for any negative UTC offset (the same class of bug as the
// event-date timezone gotcha this codebase has hit before). A pure string parse never constructs
// a Date object, so it can't drift with the viewer's timezone.
export function formatEventDateShort(isoDate) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(isoDate || ""))
  if (!m) return isoDate || ""
  const month = MONTH_ABBR[Number(m[2]) - 1] || m[2]
  return `${month} ${Number(m[3])}, ${m[1]}`
}

// Client-side name filter for the picker's type-ahead (NewAgentForm shows the input only once
// there are enough events that scrolling to find one is a real problem).
export function filterEventsByQuery(events, query) {
  const q = String(query || "").trim().toLowerCase()
  if (!q) return events
  return events.filter((ev) => String((ev && ev.name) || "").toLowerCase().includes(q))
}

// ── Item #6: form-grammar expressibility of a library rawTrigger ────────────────────────────
// The library form now shows the FULL trigger UI for every seed (identical to direct create).
// A seed is EXPRESSIBLE when the form grammar can re-derive its exact trigger_spec — then the
// panels render interactive, pre-selected to the returned { freq, time } / { event } / { cond }
// cfg, and touching them (triggerDirty) swaps submit from the verbatim rawTrigger passthrough to
// normal derivation (which, by this function's deep-equality contract, produces the SAME spec
// until the user actually picks something different). A seed is INEXPRESSIBLE (returns null) when
// derivation would silently change behavior — off-list freq/time (e.g. monthly on a day other than
// the 1st/15th), or a rule_key/
// rule_params outside COND_RULE_MAP — and the form renders its trigger READ-ONLY (disabled kind
// tiles + the accurate trigger_label) so 'touched' is impossible; submit stays verbatim
// passthrough. This is what makes "no silent nearest-neighbor mapping" true.

// Key-order-independent deep compare for the plain-JSON rule_params shapes this module handles.
function stableStringify(v) {
  if (Array.isArray(v)) return `[${v.map(stableStringify).join(",")}]`
  if (v && typeof v === "object") {
    return `{${Object.keys(v).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(v[k])}`).join(",")}}`
  }
  return JSON.stringify(v)
}

// The COND_RULE_MAP option label for a rule_key, or null when no option maps to it.
export function condOptionForRuleKey(ruleKey) {
  for (const [label, rule] of Object.entries(COND_RULE_MAP)) {
    if (rule.ruleKey === ruleKey) return label
  }
  return null
}

// formCfgForRawTrigger(kind, rawTrigger) -> the { freq, time } / { event } / { cond } prefill when
// the form grammar re-derives this EXACT spec, else null (read-only render). event_id is excluded
// from the condition comparison — it's threaded separately from the picker's eventId state.
export function formCfgForRawTrigger(kind, rawTrigger) {
  const raw = rawTrigger || {}
  if (kind === "schedule") {
    const extraneous = Object.keys(raw).some((k) => k !== "freq" && k !== "time")
    if (extraneous || !SUPPORTED_SCHED_FREQ.includes(raw.freq) || !SUPPORTED_SCHED_TIME.includes(raw.time)) return null
    return { freq: raw.freq, time: raw.time }
  }
  if (kind === "event") {
    if (raw.event !== SUPPORTED_EVENT_OPT || Object.keys(raw).length !== 1) return null
    return { event: raw.event }
  }
  if (kind === "condition") {
    const label = condOptionForRuleKey(raw.rule_key)
    if (!label) return null
    const extraneous = Object.keys(raw).some((k) => !["rule_key", "rule_params", "event_id"].includes(k))
    if (extraneous) return null
    if (stableStringify(raw.rule_params || {}) !== stableStringify(COND_RULE_MAP[label].ruleParams)) return null
    return { cond: label }
  }
  if (kind === "chat") return {} // no trigger config — trivially expressible
  return null
}

export const COND_RULE_MAP = Object.freeze({
  "When an event is <50% sold with <14 days left": Object.freeze({
    ruleKey: "pct_sold_below_with_days_left", ruleParams: Object.freeze({ pct: 0.5, days: 14 }),
  }),
  "When a channel ROAS drops below 2.0x for 3 days": Object.freeze({
    ruleKey: "roas_below", ruleParams: Object.freeze({ threshold: 2.0, window: "last_3d" }),
  }),
  "When an event sells out": Object.freeze({
    ruleKey: "sold_out", ruleParams: Object.freeze({}),
  }),
})

// buildAgentJobPayload({ name, kind, icon, action, scope, cfg, apiCondition }) → { ok:true, body }
// for a supported trigger selection, or { ok:false, reason } for a known-unsupported one. cfg is the
// builder's { freq, time, event, cond } state. Never throws.
//
// D16: `apiCondition` (optional `{ ruleKey, ruleParams }`) is the API-SOURCED escape hatch for
// editing a condition job whose ORIGINAL rule_key has no COND_RULE_MAP label match (e.g. it was
// created by the agent tool, or its label drifted) — the caller (NewAgentForm) passes the job's own
// stored rule_key/rule_params through untouched instead of label-matching cfg.cond against
// COND_RULE_MAP. This is a DIRECT pass-through (never re-derived from a label) and always ok:true.
// D-T9-d (Task 9): `rawTrigger` is the library passthrough — the trigger_spec VERBATIM in the raw
// form AgentJobs::TriggerSpec.normalize accepts (a library job carries it so neither the form
// grammar nor the agent re-derives the trigger). It has TOP precedence: when present, triggerSpec =
// rawTrigger untouched and ok:true — the server normalize is the real validator (a bad spec 422s and
// the form shows it, fail-closed). Mirrors the D16 apiCondition passthrough exactly one level more
// general.
//
// A3: `modelId`/`effort` (optional) thread the builder's Model/Effort override control into
// body.agent_job — model_id is included whenever modelId is given; effort is included only when
// availableEfforts(modelId) is non-empty (mirrors studioShellViewModel.buildSendBody's per-model
// effort gating: Haiku never sends an effort). Present in BOTH the rawTrigger branch and the built
// branch below. Omitted entirely (no key at all) when modelId is not supplied, so existing callers
// that don't pass it get a byte-identical body to before A3.
//
// A4: `eventId` (optional) is the picker's chosen event, threaded into `trigger_spec.event_id` for
// ticket-based rule_keys ONLY (TICKET_RULE_KEYS — sold_out / pct_sold_below_with_days_left), on
// BOTH the `cfg`/`apiCondition` condition branch (from-scratch/edit) AND the `rawTrigger` branch
// (library launch, e.g. the flagship `sellout-watch` template). An account-level rule (roas_below,
// spend_cap_exceeded) or a non-condition rawTrigger (schedule/event) NEVER gets an event_id key —
// merged in only when the rule_key qualifies AND eventId is actually given, so every existing caller
// that doesn't pass eventId gets a byte-identical trigger_spec to before A4 (D9-style non-drift).
// Submission is NOT blocked here when a ticket rule has no eventId yet (fail-open by design — the
// picker's UI-level `valid` gate in NewAgentForm is what blocks the Create button/POST for that
// case, AC-8) — this keeps the mapper a pure, always-emits function like every other branch here.
export function buildAgentJobPayload({ name, kind, icon, action, scope, cfg, apiCondition, rawTrigger, modelId, effort, eventId,
                                       context, isEdit, hasContextKey, isNotesOnly }) {
  const c = cfg || {}
  let triggerSpec

  const modelFields = {}
  if (modelId) {
    modelFields.model_id = modelId
    if (effort && availableEfforts(modelId).length > 0) modelFields.effort = effort
  }

  // NEXT Item 4 — MODE- AND SHAPE-DEPENDENT key presence (PLAN-NEXT §D.6). The server's guard is
  // "a key that is present replaces; a key that is absent preserves", so what this function decides
  // is whether a browser save can touch the stored context at all.
  //   CREATE: emit only when non-empty — the file's non-drift convention (see the modelFields note
  //           above): existing callers get a byte-identical body.
  //   EDIT, notes-only:  ALWAYS emit, INCLUDING context: "" for an emptied textarea. Without this
  //           the guard would make the Specifics field un-clearable.
  //   EDIT, structured OR seed with no context key: NEVER emit. Structured context is agent-owned
  //           (edited via update_agent_job); the keyless case is the M-R4-1 fail-safe.
  const contextFields = {}
  if (isEdit) {
    if (hasContextKey && isNotesOnly) contextFields.context = context == null ? "" : context
  } else if (context != null && String(context).trim().length > 0) {
    contextFields.context = context
  }

  if (rawTrigger) {
    const spec = TICKET_RULE_KEYS.has(rawTrigger.rule_key) && eventId
      ? { ...rawTrigger, event_id: eventId }
      : rawTrigger
    return {
      ok: true,
      body: {
        agent_job: { name, kind, action, icon, scope: (scope || []).slice(), trigger_spec: spec, ...modelFields, ...contextFields },
      },
    }
  }

  if (kind === "schedule") {
    if (!SUPPORTED_SCHED_FREQ.includes(c.freq)) {
      return { ok: false, reason: "That schedule isn't supported yet — pick a daily, weekly, or monthly (1st/15th) option instead." }
    }
    triggerSpec = { freq: c.freq, time: c.time }
  } else if (kind === "event") {
    if (c.event !== SUPPORTED_EVENT_OPT) {
      return { ok: false, reason: "That trigger isn't supported yet — only \"When an event is published in Vesta\" is available today." }
    }
    triggerSpec = { event: c.event }
  } else if (kind === "condition") {
    if (apiCondition && apiCondition.ruleKey) {
      triggerSpec = { rule_key: apiCondition.ruleKey, rule_params: { ...(apiCondition.ruleParams || {}) } }
    } else {
      const rule = COND_RULE_MAP[c.cond]
      if (!rule) {
        return { ok: false, reason: "That condition isn't supported yet — pick one of the other rules." }
      }
      triggerSpec = { rule_key: rule.ruleKey, rule_params: { ...rule.ruleParams } }
    }
    if (TICKET_RULE_KEYS.has(triggerSpec.rule_key) && eventId) {
      triggerSpec = { ...triggerSpec, event_id: eventId }
    }
  } else if (kind === "chat") {
    // Item #6: a chat-invoked job carries NO machine trigger — the backend's normalize_chat
    // accepts {} and never seeds next_run_at/next_eval_at (the tick can't select it).
    triggerSpec = {}
  } else {
    return { ok: false, reason: "Unknown trigger kind." }
  }

  return {
    ok: true,
    body: {
      agent_job: { name, kind, action, icon, scope: (scope || []).slice(), trigger_spec: triggerSpec, ...modelFields, ...contextFields },
    },
  }
}

// buildReviewSeedPrompt(job) — the first human turn for the review chat seeded right after a
// job is created. job is the AgentJobs::Create.serialize shape (id,name,action,kind,
// trigger_spec,rule_key,scope,enabled,...). Template is VERBATIM (area-D.md §1) for the three
// machine-trigger kinds; the chat kind (Item #6 repair round, 2026-07-28) gets its own honest
// branch — a chat-invoked job has NO trigger to tighten, no timing, and it NEVER runs on its own,
// so the "will run exactly as configured even if I don't reply" preamble and the trigger
// tightness/timing review hints would both be lies there.
export function buildReviewSeedPrompt(job) {
  const j = job || {}
  const chat = j.kind === "chat"
  const triggerLine = describeTriggerSpec(j.kind, j.trigger_spec, j.rule_key)
  const scopeLabels = (j.scope || []).length
    ? j.scope.map((c) => CHANNEL_NAMES[c] || c).join(", ")
    : "all channels"
  const status = chat
    ? (j.enabled
      ? "Saved — runs when you ask for it in chat"
      : "Saved but paused — turn it on, then it runs when you ask for it in chat")
    : (j.enabled
      ? "Live — will run on its schedule/trigger"
      : "Saved but paused")

  const preamble = chat
    ? [
      "I just created a new Agent Job in Vesta — one that runs when I ask for it in chat. Please",
      "review how I set it up, tell me what looks good, and ask me 2-3 specific questions that",
      "would make it work better — think about whether the instructions are clear enough to run",
      "from a one-line mention, channel scope, and budget/approval safety. This job is already",
      "saved; it only runs when I ask for it.",
    ]
    : [
      "I just created a new Agent Job in Vesta. Please review how I set it up, tell me what looks",
      "good, and ask me 2-3 specific questions that would make it work better — think about trigger",
      "tightness, channel scope, timing, and budget/approval safety. This job is already saved and",
      "will run exactly as configured even if I don't reply.",
    ]

  return [
    ...preamble,
    ...(isSocialContentJob(j) ? ["", ...SOCIAL_CADENCE_CLARIFICATION] : []),
    "",
    reviewWriteBackLine(j),
    "",
    "Here's the job:",
    `• Name: ${j.name || ""}`,
    `• What it does: ${j.action || ""}`,
    `• Runs: ${triggerLine}   (kind: ${j.kind})`,
    `• Channels it can act on: ${scopeLabels}`,
    `• Status: ${status}`,
  ].join("\n")
}

// isSocialContentJob(job) — does this job write social content? TRUE when its scope names the
// social channel explicitly, OR when the scope is unrestricted (empty = every channel) and the
// instructions read like a posting job. A Meta-ads-only or email-only job never trips this.
const SOCIAL_ACTION_HINT = /\b(social|post|posts|posting|instagram|facebook|linkedin|tiktok|x\.com|twitter|threads)\b/i
export function isSocialContentJob(job) {
  const j = job || {}
  const scope = Array.isArray(j.scope) ? j.scope : []
  if (scope.includes("social")) return true
  return scope.length === 0 && SOCIAL_ACTION_HINT.test(String(j.action || ""))
}

// SOCIAL_CADENCE_CLARIFICATION — the two questions the agent MUST ask (and write back into the job)
// before a social-content job goes live (job-review-clarify-cadence, Billy's decision 2026-09-04).
// Why: "post about events since last run" has no lookahead window and no rule for recurring
// series, so a monthly batch job would either post about a show 3 months out or post the same
// weekly series five times. The examples are deliberately NOT a closed list — several customers
// want more than one post per occurrence — and the answers get patched into `action` with the
// update_agent_job tool so the running job actually carries them.
export const SOCIAL_CADENCE_CLARIFICATION = Object.freeze([
  "Because this job creates social posts, before anything else please ask me these two questions",
  "(one message, both questions, then wait for my answer):",
  "1. How far ahead should it look for events each time it runs?",
  "   (e.g. the next 2 weeks, the next 30 days, only events in the current month)",
  "2. How many posts per event, and how should it treat recurring event series?",
  "   Examples: one post per occurrence; one post per series (not per date);",
  "   several posts per occurrence (announce + reminder + day-of).",
  "   These are only examples — if I want something different, use what I say.",
  "Once I answer, rewrite the job's instructions to include both answers verbatim (lookahead window,",
  "posts per event, recurring-series rule) and save them — send the whole corrected instruction —",
  "then confirm what you saved.",
])

// reviewWriteBackLine(job) — the save instruction for the seeded review chat. This chat is created
// with review_job_id = job.id, which lets its update_agent_job edit THIS job even though it is
// already switched on; without an id there is nothing to link, so promise no save at all.
export function reviewWriteBackLine(job) {
  const id = job && job.id
  if (!id) return "This job was created outside this chat, so you cannot save changes to it — give me the corrected instruction text to paste in myself."
  return `This job is #${id}. You CAN edit it from this chat: call update_agent_job with id ${id} to save any change we agree on (it works here even though the job is switched on).`
}

// ── B8 — the "Standing approval" panel (design §7) ────────────────────────────────────────────
//
// A standing approval is durable money consent: one approval card, taken once, that lets future
// qualifying events be added to one campaign without a per-event approval. The panel exists so a
// customer can see at a glance what they agreed to and turn it off in one click.
//
// Pure — every string is derived here so the panel component stays presentational and the copy is
// unit-testable without rendering React.

// What each state means IN THE CUSTOMER'S TERMS. `invalidated` deliberately does not say
// "invalidated": from their side, editing the job's rules PAUSED an approval they can restore.
export const STANDING_APPROVAL_STATE_COPY = {
  active: "Active",
  invalidated: "Paused — this job's rules changed",
  revoked: "Revoked",
  superseded: "Replaced by a newer approval",
  expired: "Expired",
}

// Only these two are worth offering a Revoke button for. `superseded` and `expired` are already
// inert, and revoking them would overwrite the record of why they stopped.
const REVOCABLE_STANDING_STATES = ["active", "invalidated"]

function standingDay(value) {
  if (!value) return null
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
}

function standingMoney(usd) {
  const amount = Number(usd)
  if (!Number.isFinite(amount)) return null
  return `$${Number.isInteger(amount) ? amount : amount.toFixed(2)}/month`
}

export function buildStandingApproval(auth) {
  if (!auth) return null

  const state = auth.state || "active"
  const runs = Number(auth.executions_count) || 0
  const lastRun = standingDay(auth.last_executed_at)
  const expiry = standingDay(auth.expires_at)
  const granted = standingDay(auth.granted_at)

  return {
    id: auth.id,
    state,
    live: !!auth.live,
    stateLabel: STANDING_APPROVAL_STATE_COPY[state] || state,
    campaignLabel: auth.campaign_name
      ? `${auth.campaign_name} (${auth.campaign_id})`
      : String(auth.campaign_id || ""),
    budgetLabel: standingMoney(auth.monthly_budget_usd),
    // "0 events added" is a real, useful thing to say — never hide a counter at zero.
    runsLabel: `${runs} event${runs === 1 ? "" : "s"} added${lastRun ? ` · last ${lastRun}` : ""}`,
    grantedLabel: granted
      ? `Approved ${granted}${auth.approved_by ? ` by ${auth.approved_by}` : ""}`
      : null,
    // Mirrors the card's own §3 item 8 copy: we ask again, nothing renews itself.
    expiryLabel: expiry ? `We'll ask you to confirm again on ${expiry}` : null,
    // Only promise "live" when the customer actually granted activate_on_create.
    activationLabel: auth.activate_on_create
      ? "New events go live automatically"
      : "New events are built paused — we ask before anything goes live",
    canRevoke: REVOCABLE_STANDING_STATES.indexOf(state) !== -1,
    note: state === "invalidated"
      ? "Your standing approval paused because this job's rules changed — re-approve to resume."
      : null,
  }
}
